import React, { useEffect, useRef, useState } from 'react';
import styles from './DateTimePickerWrapper.styles';
import {
  wheelColumnWebStyle,
  wheelItemWebStyle,
  wheelTrackWebStyle,
} from './DateTimePickerWrapper.webInteractions';

const ITEM_HEIGHT = 40;
const CENTER_Y = 90;

type WheelColumnProps = {
  values: (number|string)[];
  index: number;
  onChange: (index: number) => void;
};

export default function WheelColumn({
  values,
  index,
  onChange,
}: WheelColumnProps) {
  const [offset, setOffset] = useState(CENTER_Y - index * ITEM_HEIGHT);

  const startY = useRef(0);
  const startOffset = useRef(0);
  const offsetRef = useRef(offset);
  const draggingRef = useRef(false);

  useEffect(() => {
    const nextOffset = CENTER_Y - index * ITEM_HEIGHT;
    setOffset(nextOffset);
    offsetRef.current = nextOffset;
  }, [index, values.length]);

  const snap = (newOffset: number) => {
    let idx = Math.round((CENTER_Y - newOffset) / ITEM_HEIGHT);
    idx = Math.max(0, Math.min(values.length - 1, idx));

    const snappedOffset = CENTER_Y - idx * ITEM_HEIGHT;
    setOffset(snappedOffset);
    offsetRef.current = snappedOffset;
    onChange(idx);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    draggingRef.current = true;
    startY.current = e.clientY;
    startOffset.current = offsetRef.current;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;

    const delta = e.clientY - startY.current;
    let nextOffset = startOffset.current + delta;

    const max = CENTER_Y;
    const min = CENTER_Y - (values.length - 1) * ITEM_HEIGHT;

    nextOffset = Math.max(min, Math.min(max, nextOffset));
    setOffset(nextOffset);
    offsetRef.current = nextOffset;
  };

  const endDrag = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    snap(offsetRef.current);
  };

  return (
    <div
      style={{ ...styles.wheelColumn, ...wheelColumnWebStyle }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <div
        style={{
          ...styles.wheelTrack,
          ...wheelTrackWebStyle,
          transform: `translateY(${offset}px)`,
        }}
      >
        {values.map((value, i) => (
          <div
            key={`${value}-${i}`}
            style={{
              ...styles.wheelItem,
              ...wheelItemWebStyle,
              ...(i === index ? styles.wheelItemActive : {}),
            }}
          >
            {value}
          </div>
        ))}
      </div>
    </div>
  );
}
